import React, { useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useFormik } from "formik";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AxiosService from "../../constants/api";
import {
  GET_COUNTRIES,
  GET_COMPANIES,
  UPDATE_EMPLOYEE,
  GET_EMPLOYEE_BY_ID,
} from "../../constants/endpoints";
import { profileEditFmorValidationSchema } from "../../schema";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(1),
  },
  card: {
    width: "100%",
    margin: "auto",
  },
  textField: {
    marginBottom: theme.spacing(2),
  },
}));

const EditEmployee = () => {
  const classes = useStyles();
  const { id } = useParams();
  const auth = JSON.parse(localStorage.getItem("auth"));
  //const navigate = useNavigate();

  const [countries, setCountries] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getCountries();
    getCompanies();
    getEmployeeDetails();
  }, []);

  const getCountries = async () => {
    try {
      const res = await AxiosService.post(GET_COUNTRIES);
      if (res.data.status === "OK") {
        setCountries(res.data.response);
      }
    } catch (error) {
      console.error("Error fetching countries", error);
    }
  };

  const getCompanies = async () => {
    try {
      const res = await AxiosService.post(GET_COMPANIES);
      if (res.data.status === "OK") {
        setCompanies(res.data.response);
      }
    } catch (error) {
      console.error("Error fetching companies", error);
    }
  };

  const getEmployeeDetails = async () => {
    try {
      const res = await AxiosService.post(GET_EMPLOYEE_BY_ID, { 'api_key': auth?.api_key, 'user_id': id });
      if (res.data.status === "OK") {
        const data = res.data.response;
        //console.log(data[0]);
        formik.setValues({
          f_name: data[0].f_name || "",
          l_name: data[0].l_name || "",
          email: data[0].email || "",
          password: data[0].password || "", // Do not pre-fill password
          phone: data[0].phone || "",
          c_code: data[0].c_code || "",
          comp_id: data[0].comp_id || "",
        });
      }
    } catch (error) {
      toast.error("Failed to fetch employee details");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEmployee = async (data) => {
    try {
      const payload = { ...data, 'user_id': id };
      const res = await AxiosService.post(UPDATE_EMPLOYEE, payload);
      if (res.status === 200) {
        toast.success("Employee updated successfully.");
        //navigate("/employee-list"); // change path if needed
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Update failed");
      console.error("Error updating employee", error);
    }
  };

  const formik = useFormik({
    initialValues: {
      f_name: "",
      l_name: "",
      email: "",
      password: "",
      phone: "",
      c_code: "",
      comp_id: "",
    },
    enableReinitialize: true,
    validationSchema: profileEditFmorValidationSchema,
    onSubmit: async (values) => {
      handleUpdateEmployee(values);
    },
  });

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Grid container className={classes.root}>
      <Grid item xs={12} sm={12} md={12}>
        <Card className={classes.card}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Edit Employee
            </Typography>
            <form onSubmit={formik.handleSubmit} noValidate>
              <TextField
                id="f_name"
                name="f_name"
                label="First Name"
                fullWidth
                margin="normal"
                className={classes.textField}
                value={formik.values.f_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.f_name && Boolean(formik.errors.f_name)}
                helperText={formik.touched.f_name && formik.errors.f_name}
              />
              <TextField
                id="l_name"
                name="l_name"
                label="Last Name"
                fullWidth
                margin="normal"
                className={classes.textField}
                value={formik.values.l_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.l_name && Boolean(formik.errors.l_name)}
                helperText={formik.touched.l_name && formik.errors.l_name}
              />
              <TextField
                id="phone"
                name="phone"
                label="Phone"
                fullWidth
                margin="normal"
                className={classes.textField}
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
              />
              <TextField
                id="c_code"
                name="c_code"
                label="Country Code"
                select
                fullWidth
                margin="normal"
                className={classes.textField}
                value={formik.values.c_code}
                onChange={formik.handleChange}
                error={formik.touched.c_code && Boolean(formik.errors.c_code)}
                helperText={formik.touched.c_code && formik.errors.c_code}
                // SelectProps={{ native: true }}
              >
                <option value="">Select Country Code</option>
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {`${country.code} (${country.name})`}
                  </option>
                ))}
              </TextField>
              <TextField
                id="comp_id"
                name="comp_id"
                label="Company"
                select
                fullWidth
                margin="normal"
                className={classes.textField}
                value={formik.values.comp_id}
                onChange={formik.handleChange}
                error={formik.touched.comp_id && Boolean(formik.errors.comp_id)}
                helperText={formik.touched.comp_id && formik.errors.comp_id}
                // SelectProps={{ native: true }}
              >
                <option value="">Select Company</option>
                {companies.map((company) => (
                  <option key={company.user_id} value={company.user_id}>
                    {company.name}
                  </option>
                ))}
              </TextField>
              <TextField
                id="email"
                name="email"
                label="Email"
                fullWidth
                margin="normal"
                className={classes.textField}
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
              {/* <TextField
                id="password"
                name="password"
                label="Password"
                type="password"
                fullWidth
                className={classes.textField}
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                autoComplete="new-password"
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
              /> */}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
              >
                Update
              </Button>
            </form>
          </CardContent>
          <Divider />
        </Card>
      </Grid>
    </Grid>
  );
};

export default EditEmployee;
